import { FC, useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import RightIcons from "./RightIcons";
import Icons from "../assets/Icons";
import EditIcon from "@mui/icons-material/Edit";
import RenderFilterChips from "./renderFilterChips";

type SavedFilterProps = {
  filterData: any;
  applyFilter: (isApplyAction?: boolean) => void;
  exportFilter: (filterId: string, filterName: string) => void;
  deleteFilter: (filterId: string) => void;
  setDefaultFilter: () => void;
  onChange: (text: string) => void;
  startEditing?: boolean;
};

const SavedFilter: FC<SavedFilterProps> = ({
  filterData,
  applyFilter,
  exportFilter,
  deleteFilter,
  setDefaultFilter,
  onChange,
  startEditing = false,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(filterData.filterName);
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleSave = () => {
    if (!text.trim()) return;
    exportFilter(filterData.id, text.trim());
    setIsEditing(false);
  };
  const handleApplyFilter = (isApplyFilter?: boolean) => {
    applyFilter(isApplyFilter);
  };

  const handleDelete = () => {
    deleteFilter(filterData.id);
  };

  const handleRightIcons = () => {
    if (isEditing) {
      return (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <RightIcons exportFilter={handleSave} deleteFilter={handleDelete} />
        </Box>
      );
    } else {
      return (
        <Button
          variant="outlined"
          style={{
            borderRadius: 5,
            textTransform: "none",
            fontSize: "13px",
            paddingLeft: "16px",
            paddingRight: "16px",
            borderColor: "#5921F2",
            color: "#5921F2",
          }}
          onClick={() => handleApplyFilter(true)}
        >
          {t("apply")}
        </Button>
      );
    }
  };

  const handleSelectedButtons = () => {
    if (isEditing) {
      return (
        <RightIcons exportFilter={handleSave} deleteFilter={handleDelete} />
      );
    } else {
      return (
        <Button
          variant="outlined"
          style={{
            borderRadius: 5,
            textTransform: "none",
            fontSize: "13px",
            paddingLeft: "16px",
            paddingRight: "16px",
            borderColor: "#5921F2",
            backgroundColor: "#5921F2",
            color: "#fff",
          }}
          onClick={() => handleApplyFilter(true)}
        >
          {t("Default_Filter")}
        </Button>
      );
    }
  };
  useEffect(() => {
    if (isEditing) {
      setIsHovered(false);
    }
  }, [isEditing]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsEditing(false);
        onChange(text);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, text, onChange]);
  
  useEffect(() => {
    if (startEditing) {
      setIsEditing(true);
    }
  }, [startEditing]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        border: "1px solid #1D1B20",
        borderRadius: "8px",
        mb: 1,
        position: "relative",
      }}
      onMouseEnter={() => !isEditing && setIsHovered(true)} // Only allow hover if not editing
      onMouseLeave={() => !isEditing && setIsHovered(false)} // Only reset hover if not editing
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {!isEditing ? (
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "20px",
                  cursor: "pointer",
                }}
              >
                {text}
              </Typography>
            ) : (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <TextField
                  size="small"
                  autoFocus
                  onChange={(e) => setText(e.target.value)}
                  style={{
                    width: "250px",
                  }}
                  defaultValue={filterData.filterName}
                />
              </Box>
            )}
            {!filterData.isSelected && handleRightIcons()}
          </Box>
          {filterData.isSelected && handleSelectedButtons()}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          p: 1,
          gap: 1,
        }}
      >
        <RenderFilterChips data={filterData} />
      </Box>

      {isHovered && !filterData.isSelected ? (
        <Box
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            padding: "10px",
            display: "flex",
            gap: "5px",
          }}
        >
          <IconButton
            size="small"
            style={{
              color: "gray",
              borderRadius: "4px",
            }}
            onClick={() => handleApplyFilter(false)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            style={{
              color: "#22C55E",
              borderRadius: "4px",
            }}
            onClick={handleDelete}
          >
            <Icons.DeleteIcon />
          </IconButton>
          <Button
            variant="outlined"
            onClick={setDefaultFilter}
            style={{
              paddingLeft: "14px",
              paddingRight: "14px",
              height: "40px",
              color: "custom.background.brand",
              fontWeight: 500,
              fontSize: "14px",
              border: "1px solid",
              borderColor: "custom.background.brand",
              borderRadius: "100px",
              textTransform: "none",
            }}
          >
            {t("Set_as_Default_Filter")}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default SavedFilter;
